import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from './config';
import { advanceAfterPlay } from '../utils/calendarStreak';
import { updateLeaderboardEntry } from './leaderboardService';
import { wordRungsLeaderboardScore } from '../games/WordRungs/wordRungsUtils';

function dailyPlayRef(dateId, uid) {
  return doc(db, 'dailyWordRungs', dateId, 'plays', uid);
}

function readGlobalStreakFields(data) {
  const current = (data.globalStreak ?? 0) | 0;
  const lastPlayed = data.lastPlayedGlobalDate ?? null;
  return { current, lastPlayed };
}

/**
 * Record completed daily ladder (Flutter `WordRungsRepository.recordDailyCompletion`).
 */
export async function recordWordRungsDailyCompletion({
  uid,
  dateId,
  puzzleKey,
  startWord,
  endWord,
  chain,
  optimalTransitions,
  elapsedSeconds,
  displayName,
}) {
  const transitions = chain.length - 1;
  const s = Math.min(Math.max(0, elapsedSeconds | 0), 86400);
  const score = wordRungsLeaderboardScore(transitions, s);

  const playRef = dailyPlayRef(dateId, uid);
  await setDoc(
    playRef,
    {
      puzzleKey,
      startWord,
      endWord,
      chain,
      completed: true,
      transitions,
      optimalTransitions,
      elapsedSeconds: elapsedSeconds | 0,
      score,
      recordedAt: serverTimestamp(),
    },
    { merge: true }
  );

  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return;

  const data = snap.data();
  const xs = { ...(data.wordRungsStats || {}) };
  const prevBest = (xs.bestScore ?? 0) | 0;

  const { current, lastPlayed } = readGlobalStreakFields(data);
  const global = advanceAfterPlay({
    playDate: dateId,
    lastPlayed,
    currentStreak: current,
  });
  const didAdvanceGlobal = global.streak > current || global.lastPlayed !== lastPlayed;

  xs.lastDailyCompletedDate = dateId;
  if (score > prevBest) {
    xs.bestScore = score;
  }

  const name = displayName || data.displayName || 'Player';

  await updateDoc(userRef, {
    wordRungsStats: xs,
    ...(didAdvanceGlobal
      ? {
          globalStreak: global.streak,
          lastPlayedGlobalDate: global.lastPlayed,
        }
      : {}),
    updatedAt: serverTimestamp(),
  });

  if (score > prevBest) {
    await updateLeaderboardEntry('word_rungs_best', uid, name, score);
  }
  if (didAdvanceGlobal) {
    await updateLeaderboardEntry('global_streak', uid, name, global.streak);
  }

  return { score, transitions, prevBest };
}
